import { useCreateSolarDesign } from '@/api/mutations/project/useCreateSolarDesign';
import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  RawInput,
  Text,
  Textarea,
} from '@payaca/ambrosia';
import { useEffect, useRef, useState, type FC } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod/v4';

const detailsSchema = z.object({
  name: z.string().trim().min(1, 'Enter a design name').max(120),
  description: z.string().max(2000),
});

export type SolarDesignDetails = z.infer<typeof detailsSchema>;

type DetailsFieldsProps = {
  methods: UseFormReturn<SolarDesignDetails>;
};

const DetailsFields: FC<DetailsFieldsProps> = (props) => {
  const { methods } = props;
  return (
    <Fieldset>
      <Field name="name">
        <Field.Label>Design name</Field.Label>
        <RawInput
          {...methods.register('name')}
          autoFocus
          maxLength={120}
          placeholder="e.g. South roof with battery"
        />
      </Field>
      <Controller<SolarDesignDetails, 'description'>
        name="description"
        control={methods.control}
        render={({ field: { name, ...field } }) => (
          <Field name={name}>
            <Field.Label>Description (optional)</Field.Label>
            <Textarea {...field} autoHeight={true} maxLength={2000} rows={3} />
          </Field>
        )}
      />
    </Fieldset>
  );
};

type EditSolarDesignDetailsModalProps = {
  isOpen: boolean;
  name: string;
  description?: string | null;
  unavailableReason: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (details: SolarDesignDetails) => Promise<void>;
};

export const EditSolarDesignDetailsModal: FC<
  EditSolarDesignDetailsModalProps
> = (props) => {
  const {
    isOpen,
    name,
    description,
    unavailableReason,
    isSaving,
    onClose,
    onSave,
  } = props;
  const [saveFailed, setSaveFailed] = useState(false);
  const methods = useForm<SolarDesignDetails>({
    resolver: zodResolver(detailsSchema),
    defaultValues: { name, description: description ?? '' },
  });
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      methods.reset({ name, description: description ?? '' });
      setSaveFailed(false);
    }
    wasOpen.current = isOpen;
  }, [description, isOpen, methods, name]);

  return (
    <Modal
      isOpen={isOpen}
      title="Edit design details"
      onClose={() => {
        if (!isSaving) onClose();
      }}
    >
      <Form
        methods={methods}
        onSubmit={async (details) => {
          // Recheck at submit: an autosave or remote revision may have started
          // after the modal opened.
          if (unavailableReason || isSaving) return;
          setSaveFailed(false);
          try {
            await onSave(details);
            onClose();
          } catch {
            setSaveFailed(true);
          }
        }}
      >
        <Modal.Body className="space-y-4">
          <DetailsFields methods={methods} />
          {unavailableReason && (
            <Text level="supporting" role="status">
              {unavailableReason}
            </Text>
          )}
          {saveFailed && (
            <Alert colour="red" iconName="alert-triangle.3">
              The details could not be saved. Check for newer changes and try
              again.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" disabled={isSaving} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!!unavailableReason || isSaving}
              isProcessing={isSaving}
            >
              Save details
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

type DuplicateSolarDesignModalProps = {
  isOpen: boolean;
  projectId: string;
  projectPublicId: string;
  draftId?: string;
  name: string;
  description?: string | null;
  source: ProjectSolarPotential;
  unavailableReason: string | null;
  onClose: () => void;
};

export const DuplicateSolarDesignModal: FC<DuplicateSolarDesignModalProps> = (
  props
) => {
  const {
    isOpen,
    projectId,
    projectPublicId,
    draftId,
    name,
    description,
    source,
    unavailableReason,
    onClose,
  } = props;
  const history = useHistory();
  const create = useCreateSolarDesign(projectId, projectPublicId);
  const [designId, setDesignId] = useState(() => crypto.randomUUID());
  const methods = useForm<SolarDesignDetails>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      name: `${name} (copy)`.slice(0, 120),
      description: description ?? '',
    },
  });
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setDesignId(crypto.randomUUID());
      methods.reset({
        name: `${name} (copy)`.slice(0, 120),
        description: description ?? '',
      });
      create.reset();
    }
    wasOpen.current = isOpen;
    // `create` is an unstable mutation result; including it would reset the
    // form every render while the modal is open.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [description, isOpen, methods, name]);

  return (
    <Modal
      isOpen={isOpen}
      title="Duplicate design"
      onClose={() => {
        if (!create.isPending) onClose();
      }}
    >
      <Form
        methods={methods}
        onSubmit={async (details) => {
          // The duplicate query intentionally reads the persisted draft. Do
          // not let it race a pending/dirty working layout.
          if (!draftId || unavailableReason || create.isPending) return;
          try {
            const result = await create.mutateAsync({
              ...details,
              designId,
              source,
              duplicateId: draftId,
            });
            history.push(
              `/projects/${projectId}/solar-designer?design=${result.saveSolarDesignDraft.id}`
            );
            onClose();
          } catch {
            /* The mutation state renders the error below. */
          }
        }}
      >
        <Modal.Body className="space-y-4">
          <DetailsFields methods={methods} />
          {unavailableReason && (
            <Text level="supporting" role="status">
              {unavailableReason}
            </Text>
          )}
          {create.isError && (
            <Alert colour="red" iconName="alert-triangle.3">
              The design could not be duplicated. Check that its roof analysis
              is up to date and try again.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              disabled={create.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!!unavailableReason || !draftId || create.isPending}
              isProcessing={create.isPending}
            >
              Duplicate design
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
